/** A read-only tab's section: a `SectionCard` that is also an anchor — the Actions menu
 *  and the time axis scroll to it and focus it ("What the feed sent", "View raw … data",
 *  a delivery's row). Labelled by its title, so it reads as a region. */
import { Box } from '@mui/material';
import { useEffect, type ReactNode } from 'react';

import { SectionCard } from '@/components/ReadView';
import { prefersReducedMotion } from '@/features/records/page/recordPage';

export function TabSection({
  id,
  title,
  action,
  children,
}: {
  id: string;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      component="section"
      id={id}
      tabIndex={-1}
      aria-label={title}
      sx={{
        scrollMarginTop: 16,
        outline: 'none',
        borderRadius: 1,
        mb: 2,
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
        '& > .MuiCard-root': { mb: 0 },
      }}
    >
      <SectionCard title={title} action={action}>
        {children}
      </SectionCard>
    </Box>
  );
}

/** A request to bring one section of a read-only tab into view — and, with `highlight`,
 *  one of its grid rows (`data-id`). `seq` makes a repeat request a new one. */
export interface SectionIntent<S extends string = string> {
  section: S;
  highlight?: string | null;
  seq: number;
}

/** Scrolls the intent's section (or its row) into view and focuses the section, once
 *  `ready` — when what sits above it and the row itself have rendered — then reports it
 *  done. `ids` maps a section to its anchor id. */
export function useSectionIntent<S extends string>(
  intent: SectionIntent<S> | null | undefined,
  ids: Record<S, string>,
  ready: boolean,
  onDone: (() => void) | undefined,
) {
  const seq = intent?.seq;
  const section = intent?.section;
  const highlight = intent?.highlight ?? null;
  useEffect(() => {
    if (seq === undefined || section === undefined || !ready) return undefined;
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(ids[section]);
      const row = highlight ? el?.querySelector<HTMLElement>(`[data-id="${CSS.escape(highlight)}"]`) : null;
      const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
      (row ?? el)?.scrollIntoView?.({ block: row ? 'center' : 'start', behavior });
      el?.focus({ preventScroll: true });
      onDone?.();
    });
    return () => cancelAnimationFrame(raf);
    // The request is `seq`; the ids and the callback's identity are not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seq, ready]);
}
